import React from 'react';
import styled from 'styled-components';
import { Tooltip } from '~/components';
import { useIntegrations } from '~/contexts';
import { useFeatures } from '~/hooks';
import { accountingProviders } from '~/lookups';
import qboIcon from '~/assets/public/qbo-icon.png';
import sageIcon from '~/assets/public/sage-intacct-icon.svg';
import xeroIcon from '~/assets/public/xero-icon.png';

const icons = { qbo: qboIcon, xero: xeroIcon, sage: sageIcon };

const Wrapper = styled.div`
  width: 1rem;
  height: 1rem;
  cursor: ${({ $clickable }) => ($clickable ? 'pointer' : 'default')};
`;

const Image = styled.img`
  width: 1rem;
  height: 1rem;
  display: block;
`;

function getDeepLink({ accountingProvider, accountingConnectionId, billId }, integrations) {
  switch (accountingProvider) {
    case 'xero': {
      if (!billId) return null;
      const orgCode = integrations.xero?.find((connection) => connection.id === accountingConnectionId)?.orgCode;
      return orgCode
        ? `https://go.xero.com/organisationlogin/default.aspx?shortcode=${orgCode}&redirecturl=/AccountsPayable/View.aspx?InvoiceID=${billId}`
        : `https://go.xero.com/AccountsPayable/View.aspx?InvoiceID=${billId}`;
    }

    default:
      return null;
  }
}

export default function ExpenseAccountingIndicator({
  accountingProvider,
  accountingConnectionId,
  accountingConnectionName,
  invoiceNumber,
  billNumber,
  billId,
  isSynced,
}) {
  const integrations = useIntegrations();
  const features = useFeatures();
  const hasBill = Boolean(billNumber || billId);

  if (!accountingProvider) return null;
  if (!invoiceNumber && !isSynced && !hasBill) return null;

  const accountingProviderName = accountingProviders[accountingProvider]?.name ?? accountingProvider;
  const location =
    features.multipleAccountingIntegrations && accountingConnectionName
      ? `the "${accountingConnectionName}" instance`
      : accountingProviderName;

  const billPart = billNumber ? `vendor bill #${billNumber}` : 'a vendor bill';

  const deepLink = getDeepLink({ accountingProvider, accountingConnectionId, billId }, integrations);
  const handleClick = deepLink
    ? (event) => {
        event.stopPropagation();
        window.open(deepLink, '_blank');
      }
    : undefined;

  let message;
  if (invoiceNumber && isSynced) {
    message = `This expense item is associated with invoice #${invoiceNumber} and ${billPart} in ${location}.`;
  } else if (invoiceNumber) {
    message = `This expense item is associated with invoice #${invoiceNumber} in ${location}.`;
  } else {
    message = `This expense item is associated with ${billPart} in ${location}.`;
  }

  return (
    <Wrapper $clickable={!!deepLink} onClick={handleClick}>
      <Tooltip message={message}>
        <Image src={icons[accountingProvider]} />
      </Tooltip>
    </Wrapper>
  );
}
